import React, { useCallback, useEffect, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Chip,
    CircularProgress,
    Drawer,
    IconButton,
    Stack,
    Step,
    StepContent,
    StepLabel,
    Stepper,
    Tooltip,
    Typography,
} from '@mui/material';
import { Close, ContentCopy, ExpandMore, Refresh } from '@mui/icons-material';
import { toast } from 'react-toastify';

const EXECUTION_API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/?$/, '/')}synctrack/mcp-execution`;

const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
    };
};

const formatTs = (val) => {
    if (!val) return '—';
    try {
        const d = new Date(val);
        return d.toLocaleString(undefined, {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            second: '2-digit',
        });
    } catch {
        return String(val);
    }
};

const formatDuration = (ms) => {
    if (ms == null || ms === '') return null;
    const n = Number(ms);
    if (Number.isNaN(n)) return null;
    if (n < 1000) return `${n} ms`;
    return `${(n / 1000).toFixed(1)} s`;
};

const statusColor = (status) => {
    const s = (status || '').toUpperCase();
    if (s === 'SUCCESS' || s === 'COMPLETED' || s === 'OK') return 'success';
    if (
        s === 'FAILED' ||
        s === 'ERROR' ||
        s === 'API_FAILED' ||
        s === 'VALIDATION_FAILED' ||
        s === 'TARGET_API_ERROR'
    ) {
        return 'error';
    }
    if (s === 'STARTED' || s === 'RUNNING') return 'info';
    return 'default';
};

const httpCodeFromLog = (log, detail) => {
    const code = detail?.http_status_code ?? log?.http_status_code;
    if (code == null || code === '') return null;
    const n = Number(code);
    return Number.isNaN(n) ? null : n;
};

const extractApiMessage = (targetResponse) => {
    const o = parseJsonObj(targetResponse);
    if (!o || typeof o !== 'object') return null;
    for (const key of ['message', 'detail', 'error', 'title']) {
        const val = o[key];
        if (val != null && String(val).trim()) return String(val);
    }
    return null;
};

/** User-facing outcome: separates MCP validation from target API result. */
const deriveRunOutcome = (log, detail) => {
    const rawStatus = (detail?.execution_status || log?.execution_status || '').toUpperCase();
    const validation = (detail?.validation_status || log?.validation_status || '').toUpperCase();
    const http = httpCodeFromLog(log, detail);
    const apiMessage = extractApiMessage(detail?.target_response) || detail?.error_message || log?.error_message;

    if (rawStatus === 'VALIDATION_FAILED' || validation === 'FAILED') {
        return {
            kind: 'validation_failed',
            label: 'Validation failed',
            chipLabel: 'Validation failed',
            severity: 'error',
            headline: 'Request did not pass MCP validation',
            subline: 'The target API was not called.',
        };
    }

    const apiFailed =
        rawStatus === 'API_FAILED' ||
        rawStatus === 'TARGET_API_ERROR' ||
        (http != null && http >= 400) ||
        (rawStatus === 'SUCCESS' && http != null && http >= 400);

    if (apiFailed) {
        const httpPart = http != null ? `HTTP ${http}` : 'API error';
        return {
            kind: 'api_failed',
            label: `API failed (${httpPart})`,
            chipLabel: http != null ? `API failed · ${http}` : 'API failed',
            severity: 'error',
            headline: `Target API call failed (${httpPart})`,
            subline:
                apiMessage ||
                'MCP validation and transform succeeded, but the external system rejected or could not complete the request.',
        };
    }

    if (rawStatus === 'SUCCESS' || rawStatus === 'COMPLETED') {
        return {
            kind: 'success',
            label: 'Completed',
            chipLabel: http != null ? `Completed · ${http}` : 'Completed',
            severity: 'success',
            headline: 'Run completed successfully',
            subline: validation === 'PASSED' ? 'Request validated and target API accepted the call.' : null,
        };
    }

    return {
        kind: 'unknown',
        label: log?.execution_status || 'Unknown',
        chipLabel: log?.execution_status || 'Unknown',
        severity: 'default',
        headline: null,
        subline: null,
    };
};

/** Infer list-row outcome from EXECUTE_TARGET step when header is still STARTED. */
const deriveRunOutcomeFromSteps = (steps) => {
    if (!Array.isArray(steps) || steps.length === 0) return null;
    const execStep = steps.find(
        (s) => String(s?.step_type || s?.step_name || '').toUpperCase() === 'EXECUTE_TARGET'
    );
    if (!execStep) return null;
    const http = stepHttpStatus(execStep);
    const failed =
        String(execStep?.step_status || '').toUpperCase() === 'FAILED' ||
        (http != null && http >= 400);
    if (failed) {
        const httpPart = http != null ? `HTTP ${http}` : 'API error';
        return {
            kind: 'api_failed',
            label: `API failed (${httpPart})`,
            chipLabel: http != null ? `API failed · ${http}` : 'API failed',
            severity: 'error',
            headline: `Target API call failed (${httpPart})`,
            subline: execStep?.step_message || execStep?.message || null,
        };
    }
    if (String(execStep?.step_status || '').toUpperCase() === 'SUCCESS') {
        return {
            kind: 'success',
            label: 'Completed',
            chipLabel: http != null ? `Completed · ${http}` : 'Completed',
            severity: 'success',
            headline: 'Run completed successfully',
            subline: null,
        };
    }
    return null;
};

const stepHttpStatus = (step) => {
    const out = parseJsonObj(step?.output_payload);
    if (!out || typeof out !== 'object') return null;
    const code = out.status_code ?? out.http_response?.status_code;
    if (code == null) return null;
    const n = Number(code);
    return Number.isNaN(n) ? null : n;
};

const effectiveStepStatus = (step) => {
    const name = (step?.step_name || '').toUpperCase();
    const raw = (step?.step_status || '').toUpperCase();
    if (name === 'EXECUTE_TARGET') {
        const http = stepHttpStatus(step);
        if (http != null && http >= 400) return 'FAILED';
    }
    return raw || 'UNKNOWN';
};

const humanizeStepName = (name) => {
    if (!name) return 'Step';
    return String(name)
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase());
};

const prettyJson = (text) => {
    if (text == null || text === '') return '';
    try {
        const parsed = typeof text === 'string' ? JSON.parse(text) : text;
        return JSON.stringify(parsed, null, 2);
    } catch {
        return String(text);
    }
};

const shortId = (id) => {
    if (!id) return '';
    const s = String(id);
    return s.length > 8 ? `${s.slice(0, 8)}…` : s;
};

const parseJsonObj = (text) => {
    if (text == null || text === '') return null;
    try {
        return typeof text === 'string' ? JSON.parse(text) : text;
    } catch {
        return null;
    }
};

const extractContractError = (payload) => {
    const o = parseJsonObj(payload);
    if (o && typeof o.__input_contract_error__ === 'string') {
        return o.__input_contract_error__;
    }
    return null;
};

const isStepFailed = (step) => {
    const s = effectiveStepStatus(step);
    return s === 'FAILED' || s === 'ERROR';
};

const formatStepOutputSummary = (output) => {
    const o = parseJsonObj(output);
    if (!o || typeof o !== 'object') return null;
    if (o.url) {
        return `HTTP ${o.status_code ?? '—'} · ${o.url}`;
    }
    return null;
};

const truncatePreview = (text, max = 280) => {
    const s = prettyJson(text);
    if (!s) return '';
    return s.length > max ? `${s.slice(0, max)}…` : s;
};

const PayloadBlock = ({ label, value, severity = 'default' }) => {
    if (value == null || value === '') return null;
    const bg =
        severity === 'error'
            ? 'rgba(211, 47, 47, 0.06)'
            : severity === 'warning'
              ? 'rgba(237, 108, 2, 0.06)'
              : 'background.paper';
    return (
        <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" fontWeight={600} display="block" sx={{ mb: 0.5 }}>
                {label}
            </Typography>
            <Typography
                component="pre"
                variant="caption"
                sx={{
                    m: 0,
                    p: 1,
                    bgcolor: bg,
                    borderRadius: 1,
                    overflow: 'auto',
                    maxHeight: 200,
                    fontSize: '0.72rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                }}
            >
                {prettyJson(value)}
            </Typography>
        </Box>
    );
};

const MetaRow = ({ label, value }) => {
    if (value == null || value === '') return null;
    return (
        <Typography variant="body2" color="text.secondary">
            <Box component="span" fontWeight={600} color="text.primary">
                {label}:{' '}
            </Box>
            {value}
        </Typography>
    );
};

const McpExecutionLogDrawer = ({ open, onClose, tenantId, endpointRow }) => {
    const [logsLoading, setLogsLoading] = useState(false);
    const [logs, setLogs] = useState([]);
    const [expandedId, setExpandedId] = useState(null);
    const [detailCache, setDetailCache] = useState({});
    const [detailLoadingId, setDetailLoadingId] = useState(null);

    const operationKey =
        endpointRow?.mec_operation_key || endpointRow?.tool_name || endpointRow?._tool_name || '';
    const endpointKey = endpointRow?.mec_endpoint_key || '';
    const method = endpointRow?.mec_method || '';

    const fetchLogs = useCallback(async () => {
        if (!tenantId || !operationKey) {
            setLogs([]);
            return;
        }
        setLogsLoading(true);
        try {
            const params = new URLSearchParams({
                tenant_id: String(tenantId),
                operation_key: operationKey,
                limit: '30',
            });
            // Match logs by operation/tool name only; strict endpoint_key AND hid failed runs
            // when catalog endpoint_key differs from what MCP wrote at invoke time.
            if (
                endpointKey &&
                endpointKey !== operationKey &&
                !endpointKey.match(/^\d+$/)
            ) {
                params.set('endpoint_key', endpointKey);
            }
            const res = await fetch(`${EXECUTION_API}/logs?${params}`, { headers: getAuthHeaders() });
            if (!res.ok) throw new Error(await res.text());
            const data = await res.json();
            const rows = Array.isArray(data.rows) ? data.rows : [];
            setLogs(rows);
            setDetailCache({});
            if (rows.length > 0) {
                setExpandedId(rows[0].execution_id);
            } else {
                setExpandedId(null);
            }
        } catch (e) {
            console.error(e);
            toast.error('Could not load execution history');
            setLogs([]);
            setExpandedId(null);
        } finally {
            setLogsLoading(false);
        }
    }, [tenantId, operationKey, endpointKey]);

    useEffect(() => {
        if (open) {
            setExpandedId(null);
            setDetailCache({});
            fetchLogs();
        }
    }, [open, fetchLogs]);

    useEffect(() => {
        if (!open || !expandedId || !tenantId || detailCache[expandedId]) return undefined;
        let cancelled = false;
        setDetailLoadingId(expandedId);
        (async () => {
            try {
                const params = new URLSearchParams({ tenant_id: String(tenantId) });
                const res = await fetch(
                    `${EXECUTION_API}/log/${encodeURIComponent(expandedId)}?${params}`,
                    { headers: getAuthHeaders() }
                );
                if (!res.ok) throw new Error(await res.text());
                const data = await res.json();
                if (!cancelled) {
                    setDetailCache((prev) => ({ ...prev, [expandedId]: data }));
                }
            } catch (e) {
                console.error(e);
                if (!cancelled) toast.error('Could not load run details');
            } finally {
                if (!cancelled) setDetailLoadingId(null);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open, expandedId, tenantId, detailCache]);

    const handleCopyId = (id) => {
        if (!id) return;
        navigator.clipboard.writeText(String(id)).then(
            () => toast.success('Execution ID copied'),
            () => toast.error('Could not copy')
        );
    };

    const handleAccordionChange = (executionId) => (_, isExpanded) => {
        setExpandedId(isExpanded ? executionId : null);
    };

    const titleLine = [operationKey, method].filter(Boolean).join(' · ');

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{ sx: { width: { xs: '100%', sm: 520, md: 560 }, maxWidth: '100vw' } }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'grey.50' }}>
                <Box
                    sx={{
                        px: 2,
                        py: 1.5,
                        bgcolor: 'background.paper',
                        borderBottom: 1,
                        borderColor: 'divider',
                    }}
                >
                    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                                Execution history
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                {titleLine || 'MCP endpoint'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Recent agent/MCP runs for this tenant endpoint
                            </Typography>
                        </Box>
                        <Stack direction="row">
                            <Tooltip title="Refresh">
                                <IconButton size="small" onClick={fetchLogs} disabled={logsLoading}>
                                    <Refresh fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <IconButton size="small" onClick={onClose} aria-label="Close">
                                <Close fontSize="small" />
                            </IconButton>
                        </Stack>
                    </Stack>
                </Box>

                <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                    {logsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                            <CircularProgress size={32} />
                        </Box>
                    ) : logs.length === 0 ? (
                        <Alert severity="info">
                            No runs recorded yet. Trigger this operation from Sync Hub or an agent, then refresh.
                        </Alert>
                    ) : (
                        <Stack spacing={1.5}>
                            {logs.map((log, index) => {
                                const id = log.execution_id;
                                const isExpanded = expandedId === id;
                                const detail = detailCache[id];
                                const loadingDetail = detailLoadingId === id;
                                const steps = Array.isArray(detail?.steps) ? detail.steps : [];
                                const duration = formatDuration(
                                    log.execution_duration_ms ?? detail?.execution_duration_ms
                                );
                                let outcome = deriveRunOutcome(log, detail);
                                if (outcome.kind === 'unknown' && steps.length > 0) {
                                    const fromSteps = deriveRunOutcomeFromSteps(steps);
                                    if (fromSteps) outcome = fromSteps;
                                }

                                return (
                                    <Accordion
                                        key={id}
                                        expanded={isExpanded}
                                        onChange={handleAccordionChange(id)}
                                        disableGutters
                                        elevation={0}
                                        sx={{
                                            bgcolor: 'background.paper',
                                            border: 1,
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                            '&:before': { display: 'none' },
                                        }}
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMore />}
                                            sx={{ px: 2, minHeight: 56 }}
                                        >
                                            <Stack spacing={0.5} sx={{ width: '100%', pr: 1 }}>
                                                <Stack
                                                    direction="row"
                                                    alignItems="center"
                                                    spacing={1}
                                                    flexWrap="wrap"
                                                >
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {index === 0 ? 'Latest run' : formatTs(log.started_at)}
                                                    </Typography>
                                                    {index === 0 && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            {formatTs(log.started_at)}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                                    <Chip
                                                        size="small"
                                                        label={outcome.chipLabel}
                                                        color={statusColor(
                                                            outcome.kind === 'success'
                                                                ? 'SUCCESS'
                                                                : outcome.kind === 'validation_failed'
                                                                  ? 'VALIDATION_FAILED'
                                                                  : outcome.kind === 'api_failed'
                                                                    ? 'API_FAILED'
                                                                    : log.execution_status
                                                        )}
                                                    />
                                                    {outcome.kind === 'api_failed' &&
                                                        (detail?.validation_status || log.validation_status) ===
                                                            'PASSED' && (
                                                            <Chip
                                                                size="small"
                                                                variant="outlined"
                                                                color="default"
                                                                label="MCP validation passed"
                                                            />
                                                        )}
                                                    {duration && (
                                                        <Chip
                                                            size="small"
                                                            variant="outlined"
                                                            label={duration}
                                                        />
                                                    )}
                                                </Stack>
                                                {outcome.headline && !isExpanded && (
                                                    <Typography
                                                        variant="caption"
                                                        color={
                                                            outcome.severity === 'error'
                                                                ? 'error.main'
                                                                : 'text.secondary'
                                                        }
                                                        sx={{
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden',
                                                        }}
                                                    >
                                                        {outcome.headline}
                                                        {extractApiMessage(detail?.target_response) &&
                                                            ` — ${extractApiMessage(detail?.target_response)}`}
                                                    </Typography>
                                                )}
                                                {log.error_message &&
                                                    !isExpanded &&
                                                    outcome.kind !== 'api_failed' && (
                                                    <Typography
                                                        variant="caption"
                                                        color="error"
                                                        sx={{
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden',
                                                        }}
                                                    >
                                                        {log.error_message}
                                                    </Typography>
                                                )}
                                            </Stack>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
                                            {loadingDetail && !detail ? (
                                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                                    <CircularProgress size={24} />
                                                </Box>
                                            ) : (
                                                <Stack spacing={2}>
                                                    {outcome.headline && (
                                                        <Alert severity={outcome.severity} sx={{ py: 0.5 }}>
                                                            <Typography variant="body2" fontWeight={600}>
                                                                {outcome.headline}
                                                            </Typography>
                                                            {outcome.subline && (
                                                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                                    {outcome.subline}
                                                                </Typography>
                                                            )}
                                                        </Alert>
                                                    )}

                                                    {(detail?.error_message || log.error_message) &&
                                                        outcome.kind !== 'api_failed' && (
                                                        <Alert severity="error" sx={{ py: 0.5 }}>
                                                            {detail?.error_code && (
                                                                <strong>{detail.error_code}: </strong>
                                                            )}
                                                            {detail?.error_message || log.error_message}
                                                        </Alert>
                                                    )}

                                                    {extractContractError(detail?.request_payload) && (
                                                        <Alert severity="warning" sx={{ py: 0.5 }}>
                                                            <Typography variant="body2" fontWeight={600} gutterBottom>
                                                                Input contract issue
                                                            </Typography>
                                                            {extractContractError(detail?.request_payload)}
                                                        </Alert>
                                                    )}

                                                    {detail?.validation_errors && (
                                                        <Alert severity="warning" sx={{ py: 0.5 }}>
                                                            <Typography variant="caption" fontWeight={600}>
                                                                Validation errors
                                                            </Typography>
                                                            <Typography variant="body2" component="div" sx={{ mt: 0.5 }}>
                                                                {prettyJson(detail.validation_errors)}
                                                            </Typography>
                                                        </Alert>
                                                    )}

                                                    <Box
                                                        sx={{
                                                            p: 1.5,
                                                            bgcolor: 'grey.50',
                                                            borderRadius: 1,
                                                            border: 1,
                                                            borderColor: 'divider',
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            fontWeight={600}
                                                            color="text.secondary"
                                                            sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
                                                        >
                                                            Run summary
                                                        </Typography>
                                                        <Stack spacing={0.5} sx={{ mt: 1 }}>
                                                            <MetaRow label="Outcome" value={outcome.label} />
                                                            <MetaRow
                                                                label="MCP validation"
                                                                value={
                                                                    detail?.validation_status ||
                                                                    log.validation_status ||
                                                                    '—'
                                                                }
                                                            />
                                                            {httpCodeFromLog(log, detail) != null && (
                                                                <MetaRow
                                                                    label="Target HTTP status"
                                                                    value={String(httpCodeFromLog(log, detail))}
                                                                />
                                                            )}
                                                            <MetaRow
                                                                label="Tool"
                                                                value={detail?.tool_name || log.tool_name}
                                                            />
                                                            <MetaRow
                                                                label="Agent"
                                                                value={detail?.tenant_agent_id || log.tenant_agent_id}
                                                            />
                                                            <MetaRow
                                                                label="Correlation"
                                                                value={detail?.correlation_id || log.correlation_id}
                                                            />
                                                        </Stack>
                                                        {detail?.target_response && (
                                                            <Box sx={{ mt: 1 }}>
                                                                <Typography variant="caption" fontWeight={600}>
                                                                    API response (preview)
                                                                </Typography>
                                                                <Typography
                                                                    variant="caption"
                                                                    component="pre"
                                                                    sx={{
                                                                        m: 0,
                                                                        mt: 0.5,
                                                                        p: 1,
                                                                        bgcolor: 'background.paper',
                                                                        borderRadius: 1,
                                                                        overflow: 'auto',
                                                                        maxHeight: 120,
                                                                        fontSize: '0.7rem',
                                                                    }}
                                                                >
                                                                    {truncatePreview(detail.target_response, 400)}
                                                                </Typography>
                                                            </Box>
                                                        )}
                                                    </Box>

                                                    <Box>
                                                        <Typography
                                                            variant="caption"
                                                            fontWeight={600}
                                                            color="text.secondary"
                                                            sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
                                                        >
                                                            What happened
                                                        </Typography>
                                                        {steps.length === 0 ? (
                                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                                No step trace for this run.
                                                            </Typography>
                                                        ) : (
                                                            <Stepper
                                                                orientation="vertical"
                                                                activeStep={steps.length}
                                                                sx={{ mt: 1 }}
                                                            >
                                                                {steps.map((step) => {
                                                                    const outSummary = formatStepOutputSummary(
                                                                        step.output_payload
                                                                    );
                                                                    const contractErr = extractContractError(
                                                                        step.input_payload
                                                                    );
                                                                    const showPayloads =
                                                                        isStepFailed(step) ||
                                                                        contractErr ||
                                                                        step.validation_errors ||
                                                                        step.input_payload ||
                                                                        step.output_payload;

                                                                    return (
                                                                        <Step
                                                                            key={step.step_id || step.step_sequence}
                                                                            expanded
                                                                        >
                                                                            <StepLabel
                                                                                optional={
                                                                                    <Stack alignItems="flex-end">
                                                                                        {step.duration_ms != null && (
                                                                                            <Typography variant="caption">
                                                                                                {formatDuration(
                                                                                                    step.duration_ms
                                                                                                )}
                                                                                            </Typography>
                                                                                        )}
                                                                                        {outSummary && (
                                                                                            <Typography
                                                                                                variant="caption"
                                                                                                color="text.secondary"
                                                                                                sx={{
                                                                                                    maxWidth: 220,
                                                                                                    textAlign: 'right',
                                                                                                    display: '-webkit-box',
                                                                                                    WebkitLineClamp: 2,
                                                                                                    WebkitBoxOrient:
                                                                                                        'vertical',
                                                                                                    overflow: 'hidden',
                                                                                                }}
                                                                                            >
                                                                                                {outSummary}
                                                                                            </Typography>
                                                                                        )}
                                                                                    </Stack>
                                                                                }
                                                                            >
                                                                                <Stack
                                                                                    direction="row"
                                                                                    alignItems="center"
                                                                                    spacing={1}
                                                                                    flexWrap="wrap"
                                                                                >
                                                                                    <Typography variant="body2">
                                                                                        {humanizeStepName(
                                                                                            step.step_name
                                                                                        )}
                                                                                    </Typography>
                                                                                    <Chip
                                                                                        size="small"
                                                                                        label={effectiveStepStatus(step)}
                                                                                        color={statusColor(
                                                                                            effectiveStepStatus(step)
                                                                                        )}
                                                                                        sx={{
                                                                                            height: 20,
                                                                                            fontSize: '0.7rem',
                                                                                        }}
                                                                                    />
                                                                                </Stack>
                                                                            </StepLabel>
                                                                            <StepContent>
                                                                                {step.step_message && (
                                                                                    <Typography
                                                                                        variant="body2"
                                                                                        sx={{ mb: 1 }}
                                                                                    >
                                                                                        {step.step_message}
                                                                                    </Typography>
                                                                                )}
                                                                                {contractErr && (
                                                                                    <Alert
                                                                                        severity="error"
                                                                                        sx={{ mb: 1, py: 0.5 }}
                                                                                    >
                                                                                        {contractErr}
                                                                                    </Alert>
                                                                                )}
                                                                                {step.validation_errors && (
                                                                                    <Alert
                                                                                        severity="warning"
                                                                                        sx={{ mb: 1, py: 0.5 }}
                                                                                    >
                                                                                        {prettyJson(
                                                                                            step.validation_errors
                                                                                        )}
                                                                                    </Alert>
                                                                                )}
                                                                                {showPayloads && (
                                                                                    <Box sx={{ pl: 0.5 }}>
                                                                                        <PayloadBlock
                                                                                            label="Step input"
                                                                                            value={step.input_payload}
                                                                                            severity={
                                                                                                contractErr || isStepFailed(step)
                                                                                                    ? 'error'
                                                                                                    : 'default'
                                                                                            }
                                                                                        />
                                                                                        <PayloadBlock
                                                                                            label="Step output"
                                                                                            value={step.output_payload}
                                                                                            severity={
                                                                                                isStepFailed(step)
                                                                                                    ? 'error'
                                                                                                    : 'default'
                                                                                            }
                                                                                        />
                                                                                    </Box>
                                                                                )}
                                                                            </StepContent>
                                                                        </Step>
                                                                    );
                                                                })}
                                                            </Stepper>
                                                        )}
                                                    </Box>

                                                    <Accordion
                                                        disableGutters
                                                        elevation={0}
                                                        sx={{
                                                            bgcolor: 'grey.100',
                                                            borderRadius: 1,
                                                            '&:before': { display: 'none' },
                                                        }}
                                                    >
                                                        <AccordionSummary expandIcon={<ExpandMore />}>
                                                            <Typography variant="caption" color="text.secondary">
                                                                Technical details (payloads &amp; ID)
                                                            </Typography>
                                                        </AccordionSummary>
                                                        <AccordionDetails>
                                                            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    Execution ID: {shortId(id)}
                                                                </Typography>
                                                                <Tooltip title="Copy full execution ID">
                                                                    <IconButton size="small" onClick={() => handleCopyId(id)}>
                                                                        <ContentCopy sx={{ fontSize: 14 }} />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </Stack>
                                                            <MetaRow label="Session" value={detail?.session_id} />
                                                            <MetaRow
                                                                label="Conversation"
                                                                value={detail?.conversation_id}
                                                            />
                                                            <PayloadBlock
                                                                label="Request payload"
                                                                value={detail?.request_payload}
                                                            />
                                                            <PayloadBlock
                                                                label="Transformed payload (sent to API)"
                                                                value={detail?.transformed_payload}
                                                            />
                                                            <PayloadBlock
                                                                label="Full API response"
                                                                value={detail?.target_response}
                                                            />
                                                        </AccordionDetails>
                                                    </Accordion>
                                                </Stack>
                                            )}
                                        </AccordionDetails>
                                    </Accordion>
                                );
                            })}
                        </Stack>
                    )}
                </Box>
            </Box>
        </Drawer>
    );
};

export default McpExecutionLogDrawer;
